// Data layer for the sidebar engine. Walks a PanelConfig, collects every inline
// { api } descriptor into a stable, id-keyed list, and runs them all through ONE
// useQueries call (TanStack Query takes a dynamic-length array in a single hook,
// so this is rules-of-hooks safe — and the panel is keyed by panelId, so it
// remounts on switch). Returns a resolved map keyed by descriptor id.

import { useQueries } from '@tanstack/react-query';
import { apiService, baseService } from '@clarix/api-client';
import type { ApiRequest, PanelConfig } from '@clarix/shared-types';
import { resolveParams } from './interpolate';

export interface ResolvedQuery {
  data: unknown;
  isLoading: boolean;
  isError: boolean;
}

export type ResolvedData = Record<string, ResolvedQuery>;

interface Descriptor {
  key: string;
  api: ApiRequest;
}

/** Deterministic pre-order collection of every ApiRequest in the config. */
export function collectDescriptors(config: PanelConfig): Descriptor[] {
  const out: Descriptor[] = [];
  const push = (key: string, api: ApiRequest | undefined) => {
    if (api) out.push({ key, api });
  };

  for (const row of config.headerRows ?? []) {
    push(`row:header:${row.id}:badge`, row.badge?.count?.api);
    row.badge?.sum?.forEach((c, i) => push(`row:header:${row.id}:badge:sum:${i}`, c.api));
  }
  for (const sec of config.sections) {
    const src = sec.rowsSource;
    push(`sec:${sec.id}:rows`, src && 'api' in src ? src.api : undefined);
    for (const row of sec.rows ?? []) {
      push(`row:${sec.id}:${row.id}:badge`, row.badge?.count?.api);
      row.badge?.sum?.forEach((c, i) => push(`row:${sec.id}:${row.id}:badge:sum:${i}`, c.api));
    }
  }
  return out;
}

const service = (req: ApiRequest) => (req.service === 'api' ? apiService : baseService);

/** Run all of a panel's inline API calls; resolve to an id-keyed data map. */
export function useSidebarData(
  config: PanelConfig,
  ctxBag: Record<string, string | number | null | undefined>,
): ResolvedData {
  const descriptors = collectDescriptors(config);

  const results = useQueries({
    queries: descriptors.map((d) => {
      const params = resolveParams(d.api.params, ctxBag);
      return {
        queryKey: ['sidebar-panel', config.id, d.key, params ?? {}],
        // Resolve to the payload that declarative dot-paths (listPath /
        // valuePath) apply to. baseService returns the raw axios response (take
        // `.data`); apiService already unwraps the `{ data }` envelope, so its
        // result IS the payload — unwrapping again would yield `undefined`.
        queryFn: () => {
          const req = service(d.api).get(d.api.endpoint, params ? { params } : undefined);
          return d.api.service === 'api'
            ? (req as Promise<unknown>)
            : (req as Promise<{ data: unknown }>).then((r) => r.data);
        },
        staleTime: d.api.staleTime ?? 30_000,
      };
    }),
  });

  const map: ResolvedData = {};
  descriptors.forEach((d, i) => {
    const r = results[i];
    map[d.key] = { data: r?.data, isLoading: r?.isLoading ?? false, isError: r?.isError ?? false };
  });
  return map;
}
